import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { TrendingUp, TrendingDown, Target, DollarSign, BarChart3, PieChart, Activity, Eye, EyeOff, Clock, Calendar, AlertCircle, LineChart, Download } from 'lucide-react';
import CelebrationNotification from '../Notifications/CelebrationNotification';
import ProfitChart from '../Charts/ProfitChart';
import WidgetCustomizer from './WidgetCustomizer';
import ExportManager from '../Export/ExportManager';

const Dashboard = ({ setActiveTab }) => {
  const { user } = useAuth();
  const [isPrivacyMode, setIsPrivacyMode] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('current_month');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [stats, setStats] = useState({
    resultadoFinal: 0,
    taxaAcerto: 0,
    volumeTotal: 0,
    roiFinal: 0,
    totalApostas: 0,
    apostasGreen: 0,
    apostasRed: 0,
    apostasVoid: 0,
    apostasCashout: 0
  });
  const [expenses, setExpenses] = useState(0);
  const [totalWithdrawals, setTotalWithdrawals] = useState(0);
  const [recentBets, setRecentBets] = useState([]);
  const [pendingBets, setPendingBets] = useState([]);
  const [groups, setGroups] = useState([]);
  const [bettingHouses, setBettingHouses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCelebrationNotification, setShowCelebrationNotification] = useState(false);
  const [chartType, setChartType] = useState('line');
  const [showExportManager, setShowExportManager] = useState(false);
  const [visibleWidgets, setVisibleWidgets] = useState(() => {
    const saved = localStorage.getItem('dashboard_widgets');
    return saved ? JSON.parse(saved) : [
      'resultado-final', 'taxa-acerto', 'volume-total', 'roi-final', 'despesas-totais', 'saques-totais',
      'distribuicao', 'period-filter', 'charts', 'apostas-recentes', 'apostas-pendentes'
    ];
  });

  useEffect(() => {
    if (user) {
      loadDashboardData();
    }
  }, [user, selectedPeriod, customStartDate, customEndDate]);

  const availableWidgets = [
    { 
      id: 'resultado-final', 
      title: 'Resultado Final', 
      description: 'Lucro total menos despesas',
      icon: TrendingUp 
    },
    { 
      id: 'taxa-acerto', 
      title: 'Taxa de Acerto', 
      description: 'Porcentagem de apostas corretas',
      icon: Target 
    },
    { 
      id: 'volume-total', 
      title: 'Volume Total', 
      description: 'Soma total apostada',
      icon: BarChart3 
    },
    { 
      id: 'roi-final', 
      title: 'ROI Final', 
      description: 'Retorno sobre investimento',
      icon: Activity 
    },
    { 
      id: 'despesas-totais', 
      title: 'Despesas Totais', 
      description: 'Total de despesas no período',
      icon: DollarSign 
    },
    { 
      id: 'saques-totais', 
      title: 'Total Sacado', 
      description: 'Valor total sacado das bancas',
      icon: TrendingDown 
    },
    { 
      id: 'distribuicao', 
      title: 'Distribuição de Resultados', 
      description: 'Green, Red, Void, Cashout',
      icon: PieChart 
    },
    { 
      id: 'period-filter', 
      title: 'Filtro de Período', 
      description: 'Controle temporal dos dados',
      icon: Calendar 
    },
    { 
      id: 'charts', 
      title: 'Gráficos Interativos', 
      description: 'Evolução visual dos dados',
      icon: LineChart 
    },
    { 
      id: 'apostas-recentes', 
      title: 'Apostas Recentes', 
      description: 'Últimas apostas registradas',
      icon: Activity 
    },
    { 
      id: 'apostas-pendentes', 
      title: 'Apostas Pendentes', 
      description: 'Resumo de apostas em andamento',
      icon: Clock 
    }
  ];

  const handleToggleWidget = (widgetId) => {
    const newVisibleWidgets = visibleWidgets.includes(widgetId)
      ? visibleWidgets.filter(id => id !== widgetId)
      : [...visibleWidgets, widgetId];
    
    setVisibleWidgets(newVisibleWidgets);
    localStorage.setItem('dashboard_widgets', JSON.stringify(newVisibleWidgets));
  };

  const getPeriodDates = () => {
    const now = new Date();
    let startDate, endDate;

    switch (selectedPeriod) {
      case 'current_month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        // Último dia do mês às 23:59:59
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
        break;
      case 'last_month':
        startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        // Último dia do mês anterior às 23:59:59
        endDate = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
        break;
      case 'last_3_months':
        startDate = new Date(now.getFullYear(), now.getMonth() - 2, 1);
        // Último dia do mês atual às 23:59:59
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
        break;
      case 'all_time':
        return null; // No filter - show all data
      case 'custom':
        if (customStartDate && customEndDate) {
          startDate = new Date(customStartDate);
          // Adicionar 23:59:59 ao endDate para incluir o dia inteiro
          endDate = new Date(customEndDate);
          endDate.setHours(23, 59, 59, 999);
        } else {
          return null; // Don't filter if custom dates are not set
        }
        break;
      default:
        return null;
    }

    return {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString() // Incluir hora no ISO para filtro correto
    };
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Get period dates
      const periodDates = getPeriodDates();
      let statsUrl = `${process.env.REACT_APP_BACKEND_URL}/api/stats?user_id=${user.id}`;
      let betsUrl = `${process.env.REACT_APP_BACKEND_URL}/api/bets?user_id=${user.id}`;
      let expensesUrl = `${process.env.REACT_APP_BACKEND_URL}/api/expenses?user_id=${user.id}`;
      
      // Add date filters if period is selected
      if (periodDates) {
        const dateParams = `&start_date=${periodDates.startDate}&end_date=${periodDates.endDate}`;
        statsUrl += dateParams;
        betsUrl += dateParams;
        expensesUrl += dateParams;
      }
      
      // Load user stats
      const statsResponse = await fetch(statsUrl);
      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        
        // Load expenses for the final result calculation
        const expensesResponse = await fetch(expensesUrl);
        let totalExpenses = 0;
        if (expensesResponse.ok) {
          const expensesData = await expensesResponse.json();
          totalExpenses = expensesData.reduce((sum, expense) => sum + (expense.amount || 0), 0);
          setExpenses(totalExpenses);
        }

        // Load withdrawals
        let withdrawalsUrl = `${process.env.REACT_APP_BACKEND_URL}/api/withdrawals?user_id=${user.id}`;
        if (periodDates) {
          withdrawalsUrl += `&start_date=${periodDates.startDate}&end_date=${periodDates.endDate}`;
        }
        const withdrawalsResponse = await fetch(withdrawalsUrl);
        let totalWithdrawn = 0;
        if (withdrawalsResponse.ok) {
          const withdrawalsData = await withdrawalsResponse.json();
          totalWithdrawn = withdrawalsData.reduce((sum, w) => sum + (w.amount || 0), 0);
          setTotalWithdrawals(totalWithdrawn);
        }
        
        // Calculate final result (profit - expenses)
        const finalResult = (statsData.total_profit || 0) - totalExpenses;
        
        // Load recent bets with additional data
        const betsResponse = await fetch(betsUrl);
        const groupsResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/groups?user_id=${user.id}`);
        const housesResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/betting-houses?user_id=${user.id}`);
        
        if (betsResponse.ok) {
          const betsData = await betsResponse.json();
          // Get the 5 most recent bets
          const sortedBets = betsData
            .sort((a, b) => new Date(b.date) - new Date(a.date))
            .slice(0, 5);
          setRecentBets(sortedBets);
        }
        
        if (groupsResponse.ok) {
          const groupsData = await groupsResponse.json();
          setGroups(groupsData);
        }
        
        if (housesResponse.ok) {
          const housesData = await housesResponse.json();
          setBettingHouses(housesData);
        }

        // Load pending bets
        const pendingResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bets/pending?user_id=${user.id}`);
        if (pendingResponse.ok) {
          const pendingData = await pendingResponse.json();
          setPendingBets(pendingData);
        }
        
        setStats({
          resultadoFinal: finalResult,
          taxaAcerto: statsData.win_rate || 0,
          volumeTotal: statsData.total_volume || 0,
          roiFinal: statsData.roi || 0,
          totalApostas: statsData.total_bets || 0,
          apostasGreen: statsData.green_bets || 0,
          apostasRed: statsData.red_bets || 0,
          apostasVoid: statsData.void_bets || 0,
          apostasCashout: statsData.cashout_bets || 0
        });
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  const formatCurrency = (value) => {
    if (isPrivacyMode) return 'R$ •••••';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatPercentage = (value) => {
    if (isPrivacyMode) return '••••%';
    return value.toFixed(2) + '%';
  };

  const getPercentage = (value, total) => {
    if (isPrivacyMode) return '••••';
    return total > 0 ? ((value / total) * 100).toFixed(2) : '0.00';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return `${date.toLocaleDateString('pt-BR')} às ${date.toLocaleTimeString('pt-BR', { 
      hour: '2-digit', 
      minute: '2-digit'
    })}`;
  };

  const getResultColor = (result) => {
    switch (result) {
      case 'green': return 'text-green-600 dark:text-green-400';
      case 'red': return 'text-red-600 dark:text-red-400';
      case 'void': return 'text-gray-600 dark:text-gray-400';
      case 'cashout': return 'text-blue-600 dark:text-blue-400';
      default: return 'text-gray-600 dark:text-gray-400';
    }
  };

  const getResultLabel = (result) => {
    switch (result) {
      case 'green': return 'Green';
      case 'red': return 'Red';
      case 'void': return 'Void';
      case 'cashout': return 'Cashout';
      default: return result;
    }
  };

  const getResultBgColor = (result) => {
    switch (result) {
      case 'green': return 'bg-green-100 dark:bg-green-900/20';
      case 'red': return 'bg-red-100 dark:bg-red-900/20';
      case 'void': return 'bg-gray-100 dark:bg-gray-900/20';
      case 'cashout': return 'bg-blue-100 dark:bg-blue-900/20';
      default: return 'bg-gray-100 dark:bg-gray-900/20';
    }
  };

  const getGroupName = (groupId) => {
    if (!groupId) return 'Sem grupo';
    const group = groups.find(g => g.id === groupId);
    return group ? group.name : 'Grupo não encontrado';
  };

  const getBettingHouseName = (houseId) => {
    const house = bettingHouses.find(h => h.id === houseId);
    return house ? house.name : 'Casa não encontrada';
  };

  const getBetType = (bet) => {
    // Se a aposta tem campos específicos de registro diário, é diário
    // Caso contrário, é individual
    // Baseado na estrutura do backend, vou assumir que apostas individuais têm todos os campos
    // E apostas diárias podem ter campos específicos ou formato diferente
    
    // Por enquanto, vou verificar se tem comentários detalhados ou se foi um registro em lote
    if (bet.comments && bet.comments.includes('Registro diário')) {
      return 'Diário';
    }
    
    // Verificar se tem registro de horário específico ou se parece ser um registro em lote
    const betDate = new Date(bet.date);
    const now = new Date();
    const diffMinutes = Math.abs(now - betDate) / (1000 * 60);
    
    // Se a aposta foi registrada muito rapidamente após outras (mesmo dia e horário similar)
    // pode indicar registro diário, mas isso é uma heurística simples
    
    return 'Individual'; // Default para individual
  };

  return (
    <div className="space-y-6">
      {/* Header Mobile Optimized */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
            <span translate="no">Dashboard</span>
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1 text-sm sm:text-base">
            <span translate="no">Visão geral da sua performance</span>
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
          <button
            onClick={() => setShowExportManager(true)}
            className="w-full sm:w-auto flex items-center justify-center space-x-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            title="Exportar dados"
          >
            <Download className="w-5 h-5" />
            <span className="text-sm font-medium">Exportar</span>
          </button>
          <button
            onClick={() => setIsPrivacyMode(!isPrivacyMode)}
            className="w-full sm:w-auto flex items-center justify-center space-x-2 px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            title={isPrivacyMode ? 'Mostrar valores' : 'Ocultar valores'}
          >
            {isPrivacyMode ? (
              <Eye className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            ) : (
              <EyeOff className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            )}
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {isPrivacyMode ? 'Mostrar' : 'Ocultar'}
            </span>
          </button>
        </div>
      </div>

      {/* Period Filter Mobile Optimized */}
      {visibleWidgets.includes('period-filter') && (
      <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 shadow-lg border border-gray-100 dark:border-gray-700">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-3">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
            <Calendar className="w-5 h-5 mr-2 text-blue-600" />
            Período dos Dados
          </h3>
          {loading && (
            <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
              Carregando...
            </div>
          )}
        </div>
        
        <div className="flex flex-col sm:flex-row flex-wrap items-start sm:items-center gap-4">
          <div className="w-full sm:w-auto">
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="w-full sm:w-auto px-3 py-3 sm:py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="current_month">📅 Mês Atual</option>
              <option value="last_month">📆 Mês Anterior</option>
              <option value="last_3_months">📊 Últimos 3 Meses</option>
              <option value="all_time">🌐 Todo o Período</option>
              <option value="custom">🎯 Período Personalizado</option>
            </select>
          </div>
          
          {selectedPeriod === 'custom' && (
            <div className="w-full sm:w-auto flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-2">
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                className="w-full sm:w-auto px-3 py-3 sm:py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="Data inicial"
              />
              <span className="text-gray-500 dark:text-gray-400 text-center sm:text-left">até</span>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                className="w-full sm:w-auto px-3 py-3 sm:py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="Data final"
              />
            </div>
          )}
          
          <div className="text-sm text-gray-600 dark:text-gray-400">
            {selectedPeriod === 'current_month' && 'Mostrando dados do mês atual'}
            {selectedPeriod === 'last_month' && 'Mostrando dados do mês passado'}
            {selectedPeriod === 'last_3_months' && 'Mostrando dados dos últimos 3 meses'}
            {selectedPeriod === 'all_time' && 'Mostrando dados de todo o período'}
            {selectedPeriod === 'custom' && customStartDate && customEndDate && 
              `Período: ${new Date(customStartDate).toLocaleDateString('pt-BR')} - ${new Date(customEndDate).toLocaleDateString('pt-BR')}`
            }
          </div>
        </div>
      </div>
      )}

      {/* Cards de Estatísticas Mobile Optimized */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-6">
        {/* Resultado Final */}
        {visibleWidgets.includes('resultado-final') && (
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-gray-700">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <span className="text-sm font-medium text-green-600 dark:text-green-400">
              {isPrivacyMode ? '••••%' : `${stats.resultadoFinal >= 0 ? '+' : ''}${((stats.resultadoFinal / stats.volumeTotal) * 100).toFixed(2)}%`}
            </span>
          </div>
          <h3 className="text-gray-600 dark:text-gray-400 text-sm font-medium mb-1">
            <span translate="no">Resultado Final</span>
          </h3>
          <p className={`text-2xl font-bold ${stats.resultadoFinal >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
            {formatCurrency(stats.resultadoFinal)}
          </p>
          </div>
        )}

        {/* Taxa de Acerto */}
        {visibleWidgets.includes('taxa-acerto') && (
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-gray-700">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-cyan-600 rounded-lg flex items-center justify-center">
              <Target className="w-6 h-6 text-white" />
            </div>
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
              <span translate="no">{isPrivacyMode ? '••/••' : `${stats.apostasGreen}/${stats.totalApostas}`} apostas</span>
            </span>
          </div>
          <h3 className="text-gray-600 dark:text-gray-400 text-sm font-medium mb-1">
            <span translate="no">Taxa de Acerto</span>
          </h3>
          <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
            {formatPercentage(stats.taxaAcerto)}
          </p>
          </div>
        )}

        {/* Volume Total */}
        {visibleWidgets.includes('volume-total') && (
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-gray-700">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-600 rounded-lg flex items-center justify-center">
              <BarChart3 className="w-6 h-6 text-white" />
            </div>
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
              <span translate="no">{isPrivacyMode ? '••' : stats.totalApostas} apostas</span>
            </span>
          </div>
          <h3 className="text-gray-600 dark:text-gray-400 text-sm font-medium mb-1">
            <span translate="no">Volume Total</span>
          </h3>
          <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
            {formatCurrency(stats.volumeTotal)}
          </p>
          </div>
        )}

        {/* ROI Final */}
        {visibleWidgets.includes('roi-final') && (
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-gray-700">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-red-600 rounded-lg flex items-center justify-center">
              <Activity className="w-6 h-6 text-white" />
            </div>
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
              <span translate="no">{stats.roiFinal >= 0 ? 'Positivo' : 'Negativo'}</span>
            </span>
          </div>
          <h3 className="text-gray-600 dark:text-gray-400 text-sm font-medium mb-1">
            <span translate="no">ROI Final</span>
          </h3>
          <p className={`text-2xl font-bold ${stats.roiFinal >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
            {formatPercentage(stats.roiFinal)}
          </p>
          </div>
        )}

        {/* Despesas Totais */}
        {visibleWidgets.includes('despesas-totais') && (
          <div 
            onClick={() => setActiveTab?.('expenses')}
            className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-gray-700 cursor-pointer hover:scale-105">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <div className="w-12 h-12 bg-gradient-to-r from-red-500 to-pink-600 rounded-lg flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-white" />
            </div>
            <span className="text-sm font-medium text-red-600 dark:text-red-400">
              💸 Despesas
            </span>
          </div>
          <h3 className="text-gray-600 dark:text-gray-400 text-sm font-medium mb-1">
            <span translate="no">Total de Despesas</span>
          </h3>
          <p className="text-2xl font-bold text-red-600 dark:text-red-400">
            {formatCurrency(expenses)}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Clique para gerenciar
          </p>
          </div>
        )}

        {/* Total Sacado */}
        {visibleWidgets.includes('saques-totais') && (
          <div 
            onClick={() => setActiveTab?.('bankroll')}
            className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-gray-700 cursor-pointer hover:scale-105">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <div className="w-12 h-12 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center">
              <TrendingDown className="w-6 h-6 text-white" />
            </div>
            <span className="text-sm font-medium text-cyan-600 dark:text-cyan-400">
              💰 Saques
            </span>
          </div>
          <h3 className="text-gray-600 dark:text-gray-400 text-sm font-medium mb-1">
            <span translate="no">Total Sacado</span>
          </h3>
          <p className="text-2xl font-bold text-cyan-600 dark:text-cyan-400">
            {formatCurrency(totalWithdrawals)}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Clique para gerenciar
          </p>
          </div>
        )}
      </div>

      {/* Seção inferior com 2 colunas */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Distribuição de Resultados */}
        {visibleWidgets.includes('distribuicao') && (
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-100 dark:border-gray-700">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              <span translate="no">Distribuição de Resultados</span>
            </h3>
            <PieChart className="w-5 h-5 text-gray-500" />
          </div>
          
          <div className="space-y-4">
            {/* Green */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-4 h-4 bg-green-500 rounded-full"></div>
                <span className="text-gray-700 dark:text-gray-300" translate="no">Green</span>
              </div>
              <div className="text-right">
                <span className="font-semibold text-gray-900 dark:text-white">{stats.apostasGreen}</span>
                <span className="text-sm text-gray-500 ml-2">
                  ({isPrivacyMode ? '••••%' : `${getPercentage(stats.apostasGreen, stats.totalApostas)}%`})
                </span>
              </div>
            </div>

            {/* Red */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-4 h-4 bg-red-500 rounded-full"></div>
                <span className="text-gray-700 dark:text-gray-300" translate="no">Red</span>
              </div>
              <div className="text-right">
                <span className="font-semibold text-gray-900 dark:text-white">{stats.apostasRed}</span>
                <span className="text-sm text-gray-500 ml-2">
                  ({isPrivacyMode ? '••••%' : `${getPercentage(stats.apostasRed, stats.totalApostas)}%`})
                </span>
              </div>
            </div>

            {/* Cashout */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-4 h-4 bg-blue-500 rounded-full"></div>
                <span className="text-gray-700 dark:text-gray-300" translate="no">Cashout</span>
              </div>
              <div className="text-right">
                <span className="font-semibold text-gray-900 dark:text-white">{stats.apostasCashout}</span>
                <span className="text-sm text-gray-500 ml-2">
                  ({isPrivacyMode ? '••••%' : `${getPercentage(stats.apostasCashout, stats.totalApostas)}%`})
                </span>
              </div>
            </div>
          </div>
        </div>
        )}

        {/* Apostas Recentes */}
        {visibleWidgets.includes('apostas-recentes') && (
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-100 dark:border-gray-700">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              <span translate="no">Apostas Recentes</span>
            </h3>
            <Activity className="w-5 h-5 text-gray-500" />
          </div>
          
          <div className="space-y-3">
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-gray-500 mt-2">Carregando...</p>
              </div>
            ) : recentBets.length === 0 ? (
              <div className="text-center py-8">
                <Activity className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500" translate="no">
                  Nenhuma aposta registrada ainda
                </p>
              </div>
            ) : (
              recentBets.map((bet, index) => (
                <div key={bet.id || index} className={`p-4 rounded-lg border-l-4 ${
                  bet.result === 'green' ? 'border-green-500 bg-green-50 dark:bg-green-900/10' :
                  bet.result === 'red' ? 'border-red-500 bg-red-50 dark:bg-red-900/10' :
                  bet.result === 'void' ? 'border-gray-500 bg-gray-50 dark:bg-gray-900/10' :
                  'border-blue-500 bg-blue-50 dark:bg-blue-900/10'
                }`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      {/* Header com resultado e tipo */}
                      <div className="flex items-center space-x-2 mb-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getResultColor(bet.result)} bg-white dark:bg-gray-800`}>
                          {getResultLabel(bet.result)}
                        </span>
                        <span className="px-2 py-1 rounded-full text-xs bg-purple-100 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300">
                          {getBetType(bet)}
                        </span>
                        <span className="text-xs text-gray-500">{formatDate(bet.date)}</span>
                      </div>
                      
                      {/* Informações principais */}
                      <div className="flex items-center space-x-4 mb-2">
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            Odd {bet.odd}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            Stake: {isPrivacyMode ? 'R$ •••' : formatCurrency(bet.stake).replace('R$ ', 'R$ ')}
                          </p>
                        </div>
                      </div>
                      
                      {/* Detalhes adicionais */}
                      <div className="flex items-center space-x-4 text-xs text-gray-500">
                        <div>
                          <span className="font-medium">Casa:</span> {getBettingHouseName(bet.betting_house_id)}
                        </div>
                        <div>
                          <span className="font-medium">Grupo:</span> {getGroupName(bet.group_id)}
                        </div>
                      </div>
                      
                      {bet.comments && (
                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-2 truncate">
                          💬 {bet.comments}
                        </p>
                      )}
                    </div>
                    
                    <div className="text-right ml-4">
                      <p className={`text-lg font-bold ${getResultColor(bet.result)}`}>
                        {isPrivacyMode ? 'R$ •••' : formatCurrency(bet.profit || 0)}
                      </p>
                      <p className="text-xs text-gray-500">
                        {bet.result === 'cashout' ? 'Encerrada' : 'Lucro'}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
        )}

        {/* Apostas Pendentes */}
        {visibleWidgets.includes('apostas-pendentes') && pendingBets.length > 0 && (
          <div className="bg-gradient-to-r from-orange-50 to-yellow-50 dark:from-orange-900/20 dark:to-yellow-900/20 border border-orange-200 dark:border-orange-800 rounded-xl p-6 shadow-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-orange-800 dark:text-orange-200 flex items-center">
                <Clock className="w-5 h-5 mr-2" />
                🕐 Apostas Pendentes ({pendingBets.length})
              </h3>
              <button
                onClick={() => setActiveTab?.('pending-bets')}
                className="text-sm text-orange-600 dark:text-orange-400 hover:text-orange-800 dark:hover:text-orange-200 font-medium"
              >
                Ver todas →
              </button>
            </div>
            
            <div className="space-y-3">
              {pendingBets.slice(0, 3).map((bet) => {
                const timeInfo = bet.game_datetime ? (() => {
                  const now = new Date();
                  const gameDate = new Date(bet.game_datetime);
                  const diffMs = gameDate - now;
                  
                  if (diffMs < 0) {
                    const pastHours = Math.abs(Math.floor(diffMs / (1000 * 60 * 60)));
                    return { text: `Há ${pastHours}h atrás`, isPast: true, isUrgent: pastHours > 24 };
                  }
                  
                  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
                  const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
                  
                  if (diffHours > 0) {
                    return { text: `Em ${diffHours}h ${diffMinutes}m`, isPast: false, isUrgent: diffHours < 2 };
                  } else {
                    return { text: `Em ${diffMinutes}m`, isPast: false, isUrgent: true };
                  }
                })() : null;
                
                return (
                  <div key={bet.id} className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-orange-200 dark:border-orange-700">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-200">
                            Pendente
                          </span>
                          
                          {timeInfo && (
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              timeInfo.isPast ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-200' :
                              timeInfo.isUrgent ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-200' :
                              'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-200'
                            }`}>
                              {timeInfo.text}
                            </span>
                          )}
                          
                          {timeInfo?.isUrgent && (
                            <AlertCircle className="w-4 h-4 text-red-500" />
                          )}
                        </div>
                        
                        <h4 className="font-medium text-gray-900 dark:text-white mb-1">
                          {bet.match_description || 'Aposta sem descrição'}
                        </h4>
                        
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          <span>Stake: {formatCurrency(bet.stake)} • </span>
                          <span>Odd: {bet.odd} • </span>
                          <span>{formatDate(bet.date)}</span>
                        </div>
                      </div>
                      
                      <div className="ml-4 text-right">
                        <p className="text-sm font-medium text-orange-600 dark:text-orange-400">
                          Potencial: {formatCurrency(bet.stake * bet.odd)}
                        </p>
                        <p className="text-xs text-gray-500">
                          Se GREEN
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
              
              {pendingBets.length > 3 && (
                <div className="text-center pt-2">
                  <button
                    onClick={() => setActiveTab?.('pending-bets')}
                    className="text-sm text-orange-600 dark:text-orange-400 hover:text-orange-800 dark:hover:text-orange-200 font-medium"
                  >
                    Ver mais {pendingBets.length - 3} apostas pendentes →
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Charts Section */}
      {visibleWidgets.includes('charts') && recentBets.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-100 dark:border-gray-700">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
              <LineChart className="w-5 h-5 mr-2 text-blue-600" />
              Análise Gráfica
            </h3>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setChartType('line')}
                className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                  chartType === 'line'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                📈 Lucro
              </button>
              <button
                onClick={() => setChartType('bar')}
                className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                  chartType === 'bar'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                📊 Volume
              </button>
            </div>
          </div>
          
          <ProfitChart 
            bets={recentBets} 
            isPrivacyMode={isPrivacyMode}
            chartType={chartType}
          />
        </div>
      )}

      {/* Celebration Notification */}
      <CelebrationNotification
        show={showCelebrationNotification}
        onClose={() => setShowCelebrationNotification(false)}
      />

      {/* Widget Customizer */}
      <WidgetCustomizer
        availableWidgets={availableWidgets}
        visibleWidgets={visibleWidgets}
        onToggleWidget={handleToggleWidget}
      />

      {/* Export Manager */}
      {showExportManager && (
        <ExportManager
          user={user}
          onClose={() => setShowExportManager(false)}
        />
      )}
    </div>
  );
};

export default Dashboard;